import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import camelcaseKeys from 'camelcase-keys';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import { getPresignedAudioUrl } from '@src/apis/requests';
import { trackEvent } from '@src/services/moengage';
import { MOE_EVENTS } from '@src/constants/moengage';
import actions from '@src/redux/actions';
import { TTS_SYNTHESIS_URL } from '@src/configs';
import { WS_TYPE, PING_INTERVAL } from '@src/constants/websocket';
import { REQUEST_STATUS } from '@src/constants/voice';
import { PLAYING_AUDIO_TYPE } from '@src/constants/tts';

const usePronunciationPreview = ({ getCaptchaTokens, onError } = {}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const { keycloak } = useCustomKeycloak();
  const voice = useSelector((state) => state.synthesisRequest.voice);
  const showAudioPlayer = useSelector(
    (state) => state.audioPlayer.showAudioPlayer,
  );
  const { speed, audioType, bitrate, volume } = useSelector(
    (state) => state.synthesisRequest.synthesisRequest,
  );

  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const handleError = useCallback(
    (errorCode) => {
      setIsPreviewLoading(false);
      cleanup();
      if (onError) onError(errorCode);
    },
    [cleanup, onError],
  );

  const playAudioInPlayer = useCallback(
    (audioLink) => {
      dispatch(
        actions.audioPlayer.updatePreviewRequestInfo({
          voice,
          title: t('preview'),
        }),
      );
      if (!showAudioPlayer) {
        dispatch(actions.audioPlayer.updateShowAudioPlayer(true));
      }
      dispatch(actions.audioPlayer.updateStatusStreamAudio(false));
      dispatch(actions.audioPlayer.updateAudioLink(audioLink, true));
      dispatch(actions.audioPlayer.updateSelectedAudioRequest(''));
      dispatch(
        actions.audioPlayer.updateAudioType(PLAYING_AUDIO_TYPE.SYNTHESIS),
      );
      dispatch(actions.audioPlayer.updateTryListeningSentence({ audioLink }));
      dispatch(actions.audioPlayer.updateStatus(true));
    },
    [dispatch, showAudioPlayer, voice, t],
  );

  const preview = useCallback(
    async (text, { nsw } = {}) => {
      if (!text?.trim() || !voice?.code) return;
      dispatch(actions.audioPlayer.updateAudioLink(''));
      dispatch(actions.audioPlayer.updateSelectedAudioRequest(''));
      dispatch(actions.audioPlayer.updateStatus(false));
      dispatch(actions.audioPlayer.updateStatusStreamAudio(false));
      dispatch(actions.audioPlayer.updateAudioType(PLAYING_AUDIO_TYPE.SILENCE));
      dispatch(
        actions.audioPlayer.updateMetaData({ currentTime: 0, duration: 0 }),
      );
      setIsPreviewLoading(true);

      const payload = {
        text,
        voiceCode: voice.code,
        speed,
        audioType,
        bitrate,
        volume,
      };

      if (nsw && nsw.length > 0) {
        payload.nsw = nsw;
      }

      let captchaTokens = {};
      if (getCaptchaTokens) {
        try {
          captchaTokens = await getCaptchaTokens();
        } catch {
          // Continue without captcha tokens
        }
      }

      const ws = new WebSocket(TTS_SYNTHESIS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: WS_TYPE.INIT,
            accessToken: keycloak?.token,
          }),
        );
        pingIntervalRef.current = setInterval(() => {
          ws.send(JSON.stringify({ type: WS_TYPE.PING }));
        }, PING_INTERVAL);
      };

      ws.onmessage = async (res) => {
        const responseData = camelcaseKeys(JSON.parse(res.data), {
          deep: true,
        });
        const { type, result, status, code: errorCode } = responseData;

        if (status === 0) {
          handleError(errorCode);
          return;
        }

        if (type === WS_TYPE.INIT) {
          ws.send(
            JSON.stringify({
              type: WS_TYPE.SYNTHESIS,
              payload,
              accessToken: keycloak?.token,
              ...captchaTokens,
            }),
          );
        }

        if (type === WS_TYPE.SYNTHESIS) {
          if (result?.status === REQUEST_STATUS.SUCCESS) {
            trackEvent(MOE_EVENTS.STUDIO_PREVIEW_TTS, {
              voice_code: payload.voiceCode,
              characters: payload.text?.length || 0,
              speed: payload.speed,
              is_streaming: false,
            });
            try {
              const presignedResponse = await getPresignedAudioUrl(
                result.requestId,
              );
              const audioLink = presignedResponse?.status
                ? presignedResponse.result.audio
                : result?.audioLink;

              playAudioInPlayer(audioLink);
            } catch {
              handleError();
            }
            setIsPreviewLoading(false);
            cleanup();
          } else if (result?.status === REQUEST_STATUS.FAILURE) {
            handleError(errorCode);
          }
        }
      };

      ws.onerror = () => {
        handleError();
      };

      ws.onclose = () => {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      };
    },
    [
      voice,
      speed,
      audioType,
      bitrate,
      volume,
      keycloak,
      getCaptchaTokens,
      cleanup,
      handleError,
      playAudioInPlayer,
    ],
  );

  return {
    preview,
    isPreviewLoading,
  };
};

export default usePronunciationPreview;
